import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { KanbanSquare, Plus, FolderKanban, Inbox, ChevronDown, Search, Settings, LayoutDashboard } from "lucide-react";
import { cn } from "@slumbr/ui";

import { useBriefStore } from "../store";
import { SearchPalette } from "./search-palette";

export function BriefLayout() {
  const navigate = useNavigate();
  const {
    workspaces, activeWorkspaceId, projects, loadingWorkspaces,
    loadWorkspaces, setActiveWorkspace, addWorkspace, addProject,
  } = useBriefStore();
  const [wsMenuOpen, setWsMenuOpen] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => { void loadWorkspaces(); }, [loadWorkspaces]);

  // Cmd/Ctrl+K opens the search palette from anywhere in Brief.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const activeWs = workspaces.find((w) => w.id === activeWorkspaceId) ?? null;

  const handleNewWorkspace = async () => {
    const name = window.prompt("Workspace name");
    if (!name?.trim()) return;
    const ws = await addWorkspace(name.trim());
    if (ws) navigate("/brief");
  };

  const handleCreateProject = async () => {
    if (!activeWorkspaceId || !projectName.trim()) return;
    const p = await addProject(activeWorkspaceId, projectName.trim());
    setProjectName("");
    setCreatingProject(false);
    if (p) navigate(`/brief/projects/${p.id}`);
  };

  return (
    <div className="flex h-full min-h-0 w-full">
      {/* Sub-sidebar */}
      <aside className="flex w-60 shrink-0 flex-col border-r border-black/10 bg-white/60 dark:border-white/10 dark:bg-zinc-900/40">
        <div className="relative border-b border-black/10 p-3 dark:border-white/10">
          <button
            onClick={() => setWsMenuOpen((o) => !o)}
            className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm font-semibold hover:bg-black/5 dark:hover:bg-white/5"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#4C8DFF] text-white">
              {activeWs?.icon ?? <KanbanSquare className="h-4 w-4" />}
            </span>
            <span className="flex-1 truncate">{activeWs?.name ?? "Brief"}</span>
            <ChevronDown className="h-4 w-4 opacity-60" />
          </button>
          {wsMenuOpen && (
            <div className="absolute left-3 right-3 top-full z-20 mt-1 rounded-lg border border-black/10 bg-white p-1 shadow-lg dark:border-white/10 dark:bg-zinc-800">
              {workspaces.map((w) => (
                <button
                  key={w.id}
                  onClick={() => { void setActiveWorkspace(w.id); setWsMenuOpen(false); navigate("/brief"); }}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-black/5 dark:hover:bg-white/5",
                    w.id === activeWorkspaceId && "font-semibold text-[#4C8DFF]"
                  )}
                >
                  <span>{w.icon ?? "🗂️"}</span>
                  <span className="flex-1 truncate">{w.name}</span>
                </button>
              ))}
              <button
                onClick={() => { setWsMenuOpen(false); void handleNewWorkspace(); }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-[#4C8DFF] hover:bg-black/5 dark:hover:bg-white/5"
              >
                <Plus className="h-4 w-4" /> New workspace
              </button>
            </div>
          )}
        </div>

        <NavLink
          to="/brief/my-work"
          className={({ isActive }) =>
            cn(
              "mx-2 mt-2 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5",
              isActive && "bg-[#4C8DFF]/10 text-[#4C8DFF]"
            )
          }
        >
          <Inbox className="h-4 w-4" /> My Work
        </NavLink>
        <NavLink
          to="/brief/portfolio"
          className={({ isActive }) =>
            cn(
              "mx-2 mt-1 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5",
              isActive && "bg-[#4C8DFF]/10 text-[#4C8DFF]"
            )
          }
        >
          <LayoutDashboard className="h-4 w-4" /> Portfolio
        </NavLink>

        <div className="mt-4 flex items-center justify-between px-4 text-xs font-semibold uppercase tracking-wide opacity-50">
          <span>Projects</span>
          <button onClick={() => setCreatingProject((c) => !c)} className="rounded p-0.5 hover:bg-black/10 dark:hover:bg-white/10">
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
        {creatingProject && (
          <div className="px-3 py-2">
            <input
              autoFocus
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void handleCreateProject(); if (e.key === "Escape") setCreatingProject(false); }}
              placeholder="Project name"
              className="w-full rounded-md border border-black/10 bg-white px-2 py-1.5 text-sm outline-none focus:border-[#4C8DFF] dark:border-white/10 dark:bg-zinc-800"
            />
          </div>
        )}
        <nav className="flex-1 overflow-y-auto px-2 pb-4">
          {loadingWorkspaces ? (
            <p className="px-3 py-2 text-sm opacity-50">Loading…</p>
          ) : projects.length === 0 ? (
            <p className="px-3 py-2 text-sm opacity-50">No projects yet</p>
          ) : (
            projects.map((p) => (
              <NavLink
                key={p.id}
                to={`/brief/projects/${p.id}`}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-black/5 dark:hover:bg-white/5",
                    isActive && "bg-[#4C8DFF]/10 font-medium text-[#4C8DFF]"
                  )
                }
              >
                <FolderKanban className="h-4 w-4 opacity-70" />
                <span className="flex-1 truncate">{p.name}</span>
              </NavLink>
            ))
          )}
        </nav>
      </aside>

      {/* Main */}
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div className="flex shrink-0 items-center justify-end gap-2 border-b border-black/10 px-4 py-2 dark:border-white/10">
          <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-2 rounded-lg border border-black/10 px-3 py-1.5 text-sm opacity-70 hover:opacity-100 dark:border-white/10"
          >
            <Search className="h-4 w-4" /> Search
            <kbd className="ml-1 hidden rounded bg-black/10 px-1.5 text-[10px] sm:inline dark:bg-white/10">⌘K</kbd>
          </button>
          {activeWs && (
            <button
              onClick={() => navigate("/brief/settings")}
              className="rounded-lg p-2 opacity-70 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/5"
              title="Workspace settings"
            >
              <Settings className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-hidden">
          <Outlet />
        </div>
      </main>

      {searchOpen && <SearchPalette onClose={() => setSearchOpen(false)} />}
    </div>
  );
}
